/**
 * @file sanitization.ts
 * @description HTML escaping and user text sanitization utilities.
 * @author [F. Andy Seidl](https://www.linkedin.com/in/faseidl/)
 * @license UNLICENSED
 */

/**
 * Canonical recommended maximum length for SEO teaser descriptions.
 */
export const RECOMMENDED_SEO_DESCRIPTION_MAX_LENGTH = 160;

/*======================================================================
 * Pre-compiled Regular Expressions for High-Performance Sanitization
 *======================================================================*/

/**
 * Truncation ellipsis string appended to abbreviated meta descriptions.
 */
const TRUNCATION_ELLIPSIS = '...';

/**
 * Character length of the truncation ellipsis.
 */
const TRUNCATION_ELLIPSIS_LENGTH = TRUNCATION_ELLIPSIS.length;

/**
 * Matches HTML <script> elements and their executable contents.
 */
const SCRIPT_TAG_REGEX = /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi;

/**
 * Matches HTML <style> elements and embedded styles.
 */
const STYLE_TAG_REGEX = /<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi;

/**
 * Matches raw HTML tags.
 */
const HTML_TAG_REGEX = /<[^>]+>/g;

/**
 * Matches multi-line fenced code blocks, retaining internal code.
 */
const FENCED_CODE_BLOCK_REGEX = /```[\w-]*\n?([\s\S]*?)```/g;

/**
 * Matches inline backtick code snippets.
 */
const INLINE_CODE_REGEX = /`([^`]+)`/g;

/**
 * Matches Markdown image embeddings (![alt](url)).
 */
const MARKDOWN_IMAGE_REGEX = /!\[[^\]]*\]\([^)]*\)/g;

/**
 * Matches standard Markdown inline links ([anchor](url "title")).
 */
const MARKDOWN_LINK_REGEX = /\[([^\]]+)\]\([^)]*\)/g;

/**
 * Matches reference-style Markdown links ([anchor][ref]).
 */
const MARKDOWN_REF_LINK_REGEX = /\[([^\]]+)\]\[[^\]]*\]/g;

/**
 * Matches Setext heading underlines (=== or ---).
 */
const SETEXT_HEADING_REGEX = /^[=-]{2,}\s*$/gm;

/**
 * Matches Markdown ATX headings (# through ######).
 */
const ATX_HEADING_REGEX = /^#{1,6}\s+/gm;

/**
 * Matches Markdown blockquote leaders (>).
 */
const BLOCKQUOTE_REGEX = /(^|\s)>+\s*/g;

/**
 * Matches Markdown horizontal dividers (---, ***, ___).
 */
const HORIZONTAL_RULE_REGEX = /(^|\s)[-*_]{3,}(?=\s|$)/g;

/**
 * Matches unordered and numbered list item prefixes (*, +, -, 1.).
 */
const LIST_MARKER_REGEX = /^[\s]*([*+-]|\d+\.)\s+/gm;

/**
 * Matches combined triple bold/italic formatting (***text*** or ___text___).
 */
const BOLD_ITALIC_TRIPLE_REGEX = /(\*{3}|_{3})([^*_]+)\1/g;

/**
 * Matches bold formatting (**text** or __text__).
 */
const BOLD_DOUBLE_REGEX = /(\*{2}|_{2})([^*_]+)\1/g;

/**
 * Matches strikethrough formatting (~~text~~).
 */
const STRIKETHROUGH_REGEX = /~~([^~]+)~~/g;

/**
 * Matches single asterisk italic formatting (*text*).
 */
const ITALIC_ASTERISK_REGEX = /\*([^*]+)\*/g;

/**
 * Matches single underscore italic formatting (_text_).
 */
const ITALIC_UNDERSCORE_REGEX = /_([^_]+)_/g;

/**
 * Matches any residual isolated formatting asterisks, underscores, or tildes.
 */
const RESIDUAL_MARKDOWN_PUNCTUATION_REGEX = /[*_~]+/g;

/**
 * Matches consecutive whitespace characters.
 */
const CONSECUTIVE_WHITESPACE_REGEX = /\s+/g;

/**
 * Matches trailing punctuation and whitespace before ellipsis insertion.
 */
const TRAILING_PUNCTUATION_REGEX = /[\s,;:.-]+$/;

/*======================================================================
 * HTML & Text Sanitization Helpers
 *======================================================================*/

/**
 * Extracts a concise, plain-text SEO teaser description from Markdown-formatted notes.
 * Strips headings, inline styling, list markers, links, images, and HTML tags,
 * collapses whitespace, and cleanly truncates at word boundaries with an ellipsis.
 *
 * @param text - The raw Markdown event notes or description string.
 * @param maxLength - Maximum permitted character length (defaults to 160).
 * @returns Clean, plain-text description suitable for search engines and social unfurls.
 */
export function extractMetaDescription(
  text?: string,
  maxLength: number = RECOMMENDED_SEO_DESCRIPTION_MAX_LENGTH,
): string {
  if (typeof text !== 'string' || !text.trim()) {
    return '';
  }

  const cleaned = text
    .replace(SCRIPT_TAG_REGEX, '')
    .replace(STYLE_TAG_REGEX, '')
    .replace(HTML_TAG_REGEX, '')
    .replace(FENCED_CODE_BLOCK_REGEX, '$1')
    .replace(INLINE_CODE_REGEX, '$1')
    .replace(MARKDOWN_IMAGE_REGEX, '')
    .replace(MARKDOWN_LINK_REGEX, '$1')
    .replace(MARKDOWN_REF_LINK_REGEX, '$1')
    .replace(SETEXT_HEADING_REGEX, '')
    .replace(ATX_HEADING_REGEX, '')
    .replace(BLOCKQUOTE_REGEX, '')
    .replace(HORIZONTAL_RULE_REGEX, '')
    .replace(LIST_MARKER_REGEX, '')
    .replace(BOLD_ITALIC_TRIPLE_REGEX, '$2')
    .replace(BOLD_DOUBLE_REGEX, '$2')
    .replace(STRIKETHROUGH_REGEX, '$1')
    .replace(ITALIC_ASTERISK_REGEX, '$1')
    .replace(ITALIC_UNDERSCORE_REGEX, '$1')
    .replace(RESIDUAL_MARKDOWN_PUNCTUATION_REGEX, '')
    .replace(CONSECUTIVE_WHITESPACE_REGEX, ' ')
    .trim();

  if (!cleaned) {
    return '';
  }

  if (cleaned.length <= maxLength) {
    return cleaned;
  }

  if (maxLength <= TRUNCATION_ELLIPSIS_LENGTH) {
    return TRUNCATION_ELLIPSIS.slice(0, maxLength);
  }

  const maxContentLength = maxLength - TRUNCATION_ELLIPSIS_LENGTH;
  const candidate = cleaned.slice(0, maxContentLength);
  const nextChar = cleaned[maxContentLength];

  const wordBoundaryIndex = nextChar === ' ' ? maxContentLength : candidate.lastIndexOf(' ');

  let truncatedText: string;
  if (wordBoundaryIndex > 0) {
    truncatedText = candidate.slice(0, wordBoundaryIndex);
  } else {
    truncatedText = candidate;
  }

  truncatedText = truncatedText.replace(TRAILING_PUNCTUATION_REGEX, '');

  return truncatedText + TRUNCATION_ELLIPSIS;
}

/**
 * Re-exports zero-dependency HTML escaping helpers.
 */
export { escapeHtml, escapeHtmlAttribute } from './html-escape.js';

/**
 * Validates whether an outbound destination URL strictly uses the HTTPS protocol.
 *
 * @param rawUrl - URL string to validate.
 * @returns True if rawUrl is a syntactically valid HTTPS URL.
 */
export function isValidHttpsUrl(rawUrl: string): boolean {
  if (typeof rawUrl !== 'string' || !rawUrl.trim()) return false;
  try {
    const parsed = new URL(rawUrl);
    return parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Sanitizes input text by neutralizing potentially harmful tags or script injections.
 *
 * @param text - The raw user input string.
 * @returns Sanitized plain text string.
 */
export function sanitizeText(text: string): string {
  return text
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<[^>]+>/g, '')
    .trim();
}

/**
 * Escapes characters unsafe for XML/SVG documents.
 *
 * @param unsafe - Unescaped string input.
 * @returns XML-escaped string.
 */
export function escapeXml(unsafe: string): string {
  return unsafe
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}
